import React, { useState, useEffect, useRef } from 'react';
import { FaTimes, FaChevronDown, FaInfoCircle } from 'react-icons/fa';
import { IoClose } from "react-icons/io5";
import { ChatMessage } from 'models/ai-chat/chat.types';
import LoadingIndicator from './loadingIndicator';
import Avatar from './ai-avatar';
import ChatInput from './chat-input';
import { useThreadChat } from 'store/userThreadChatStore';
import { Visualization } from './visualizations';
import { extractHeadingAndContent, processMarkdownHeaders } from 'common/helpers/markdown-helper';

interface ChatDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  message: ChatMessage;
}

const ChatDrawer: React.FC<ChatDrawerProps> = ({ 
  isOpen, 
  onClose, 
  message
}) => {
  const [innerMessages, setInnerMessages] = useState<ChatMessage[]>([]);
  const [expandedMessages, setExpandedMessages] = useState<Record<string, boolean>>({});
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const { pendingMessage, startNewThreadConversation, sendChatMessage, messages, loading } = useThreadChat();
  const [isInfoVisible, setIsInfoVisible] = useState<boolean>(false);
  useEffect(() => {
    if (isOpen && message) {
      startNewThreadConversation(message.message_id);
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen, message]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
    }
  }, [innerMessages, isOpen]);

  useEffect(() => {
    setInnerMessages(messages);
    if(messages.length ==0){
      setIsInfoVisible(true)
    }
  }, [messages]);



  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleChatMessage = (msg: string) => {
    if(!msg) return;
    sendChatMessage(message.message_id, msg);
  };

  const onInformation =() =>{
    setIsInfoVisible(!isInfoVisible)
  }

  const toggleMessageExpand = (messageId: string) => {
    setExpandedMessages(prev => ({
      ...prev,
      [messageId]: !prev[messageId]
    }));
  };

  // const formatTime = (dateString: string) => {
  //   try {
  //     const date = new Date(dateString);
  //     return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  //   } catch (e) {
  //     return '';
  //   }
  // };

  // const getMessagePreview = (content: string) => {
  //   if (!content) return '';
  //   if (content.length <= 50) return content;
  //   return content.substring(0, 50) + '...';
  // };

  const formatMarkdownText = (text: string): React.ReactNode => {
    if (!text) return '';
    const parts = text.split(/(\*\*.*?\*\*)/g);
    
    return parts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        const boldText = part.substring(2, part.length - 2);
        return <strong key={index}>{boldText}</strong>;
      }
      return <React.Fragment key={index}>{part}</React.Fragment>;
    });
  };

  const renderFormattedContent = (content: string, messageId: string) => {
    // Check for JSON data first
    const jsonMatch = content.match(
      /```json\n([\s\S]*?)\n```|{\s*"headers":\s*\[[\s\S]*?"rows":\s*\[[\s\S]*?\]\s*}|Data Response:\s*([\s\S]*?)\s*Answer:/
    );
  
    if (jsonMatch) {
      try {
        const jsonString = jsonMatch[1] || jsonMatch[2] || jsonMatch[0];
        const jsonData = JSON.parse(jsonString);
  
        const jsonStartIndex = content.indexOf(jsonMatch[0]);
        const jsonEndIndex = jsonStartIndex + jsonMatch[0].length;
      //  let beforeText = content.substring(0, jsonStartIndex).trim();
        let afterText = content.substring(jsonEndIndex).trim();
  
      //  beforeText = beforeText.replace(/^Data Response:\s*/i, '').trim();
        afterText = afterText.replace(/^Answer:\s*/i, '').trim();
  
        // Handle table-type JSON (headers + rows)
        if (jsonData.headers && jsonData.rows) {
          return (
            <>
              {/* Optional: Uncomment to show any text before the table */}
              {/* {beforeText && (
                <div className="whitespace-pre-wrap mb-2">
                  {formatMarkdownText(beforeText)}
                </div>
              )} */}
  
              {/* <div className="mt-3 rounded-md overflow-hidden">
                <Visualization type="table" data={jsonData} />
              </div> */}
  
              {afterText && (
                <div className="whitespace-pre-wrap mt-2">
                  {formatMarkdownText(afterText)}
                </div>
              )}
            </>
          );
        }
  
        // ✅ Handle object-type JSON (like your "Data Response" block)
        if (typeof jsonData === 'object' && !Array.isArray(jsonData)) {
          return (
            <>
              {/* Optional: Uncomment to show any text before the JSON */}
              {/* {beforeText && (
                <div className="whitespace-pre-wrap mb-2">
                  {formatMarkdownText(beforeText)}
                </div>
              )} */}
  
              {/* <div className="mt-3 rounded-md overflow-hidden">
                <Visualization type="keyValue" data={jsonData} />
              </div>
   */}
              {afterText && (
                <div className="whitespace-pre-wrap mt-0">
                  {formatMarkdownText(afterText)}
                </div>
              )}
            </>
          );
        }
      } catch (error) {
        console.error('Error parsing JSON in content:', error);
      }
    }
  
    // If no JSON data, process headers and apply formatting
    const { title, sections } = processMarkdownHeaders(content);
    const isExpanded = expandedMessages[messageId] || false;
    const shouldShowMore = sections.length > 0;
  
    if (!shouldShowMore) {
      return (
        <div className="whitespace-pre-wrap">{formatMarkdownText(title)}</div>
      );
    }
  
    return (
      <>
        <div className="whitespace-pre-wrap">{formatMarkdownText(title)}</div>
  
        <div className={`mt-2 ${isExpanded ? 'block' : 'hidden'}`}>
          {sections.map((section, idx) => {
            const { heading, content: sectionContent } =
              extractHeadingAndContent(section);
  
            return (
              <div key={idx} className="mt-2">
                {heading && (
                  <div className="font-medium text-gray-800 mb-1 text-xs">
                    {formatMarkdownText(heading)}
                  </div>
                )}
                <div className="whitespace-pre-wrap text-xs">
                  {formatMarkdownText(sectionContent)}
                </div>
              </div>
            );
          })}
        </div>
  
        {shouldShowMore && (
          <button
            onClick={() => toggleMessageExpand(messageId)}
            title=""
            className={`custom-border absolute right-3 bottom-3 text-primary text-xs flex items-center px-2 py-1 rounded hover:bg-[#e6e6e6] transition-colors`}
          >
            {isExpanded ? 'Show less' : 'Show more'}
            <FaChevronDown
              className={`ml-1 transition-transform ${
                isExpanded ? 'rotate-180' : ''
              }`}
            />
          </button>
        )}
      </>
    );
  };
  

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="w-full md:w-[37rem] h-screen bg-white flex flex-col shadow-lg">
        <div className="pl-8 pr-3 py-4 border-b flex items-center justify-between bg-secondary-background text-white flex-shrink-0">
          <div className="flex items-center text-primary-text flex-1 pr-2">
            {/* <Avatar role={message.role} size="lg" /> */}
            <div className="text-ui truncate">
              Chat in thread
            </div>
          </div>
          {/* <button 
            onClick={onInformation}
            className="text-white hover:text-gray-300 transition-colors flex-shrink-0 mr-2"
            aria-label="Close thread"
          >
            <FaInfoCircle />
          </button> */}
          <button 
            onClick={onClose}
            className="text-primary-text transition-colors flex-shrink-0"
            aria-label="Close thread"
          >
            <IoClose className='w-5 h-5'/>
          </button>
        </div>
        {
          (!loading && isInfoVisible) &&    
          <div  className={`transition-all duration-500 overflow-hidden transform ${
            isInfoVisible ? 'max-h-40 opacity-100 translate-y-0' : 'max-h-0 opacity-0 -translate-y-2'
          } pl-8 pr-3 py-2  text-caption text-[#854D0E] bg-[#FEF08A]`}>
          <p>This chat uses only aggregated data from the table in the conversation section - it does not query the entire database.</p>
          </div>
        }
     
        <div ref={messagesContainerRef}
          className="flex-1 overflow-y-auto p-2 space-y-6"
          style={{ scrollbarWidth: 'thin' }}>

          
          <div className="space-y-5">
            {innerMessages.map((msg) => (
              <div key={msg.message_id} className="group">
                <div className={`flex items-start ${msg.role === 'user' ? 'justify-end' : ''}`}>
                  <div 
                    className={`mx-3 relative max-w-[85%] text-body text-primary-text rounded-[30px] ${
                      msg.role === 'user' 
                        ? 'bg-secondary-background' 
                        : 'bg-white'
                    } px-4 py-1.5`}
                  >
                    {/* <div className="text-ai80 text-primary-text mb-1">
                      {formatTime(msg.created_at)}
                    </div> */}
                    
                    {(msg.content && !msg.pending) && (
                      <div className="text-body text-primary-text">
                        {msg.pending}
                        {renderFormattedContent(msg.content, msg.message_id)}
                      </div>
                    )}

                    {(msg.pending) && (
                        <LoadingIndicator />
                    )}

                    
                    {msg.visualization && msg.visualization.data && msg.visualization.type !== 'text' && (
                      <div className="mt-3 rounded-md overflow-hidden">
                        <Visualization 
                          type={msg.visualization.type} 
                          data={msg.visualization.data} 
                          messageId={msg.message_id}
                          metadata={null}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          


          {loading && (
               <div className="flex justify-center items-center p-3">
               <LoadingIndicator /> 
             </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
        
        <div className="px-8 py-4 flex-shrink-0">
          <ChatInput 
            onSendMessage={handleChatMessage} 
            isLoading={pendingMessage} 
            disabled={pendingMessage}
          />
        </div>
      </div>
    </div>
  );
};

export default ChatDrawer;